import React, {Component} from "react";
import {Loading} from "../components/Loading";
import Home from "../components/Home";
import {GameEntity} from "../data/entities/GameEntity";
import {FormattedMessage} from "react-intl";
import ErrorAlert from "../components/ErrorAlert";
import {LandingPage} from "../components/LandingPage";
import {NewsItemEntity} from "../data/entities/NewsItemEntity";
import NewsService from "../data/services/NewsService";
import GamesService from "../data/services/GamesService";
import SaveFileAlertModal from "../components/SaveFileAlertModal";
import {FiltersEntity} from "../data/entities/FiltersEntity";
import {AxiosError, AxiosResponse} from "axios";
import {AdobeAnalyticsContextData} from "../context/AdobeAnalyticsContext";
import update from 'immutability-helper';
import _ from "underscore";
import {UserContext} from "../context/UserContext";
import ErrorMessage from "../components/ErrorMessage";
import {UserProgressionInfoEntity} from "../data/entities/UserProgressionInfoEntity";
import MobileLaunchModal from "../components/MobileLaunchModal";
import GamesPlayedService from "../data/services/GamesPlayedService";
import GamePlayedEntity from "../data/entities/GamePlayedEntity";
import {Route, Switch, Redirect} from "react-router-dom";
import locales from "../resources/locales";
import Anywhere from "./Anywhere/Anywhere";
import App from "./App";

interface State {
    games: GameEntity[];
    displayedGames: GameEntity[];
    gamesPlayed: GamePlayedEntity[];
    // recommendedGames: RecommendedGameEntity[];
    userProgression: UserProgressionInfoEntity | null;
    isLoading: boolean;
    filters: FiltersEntity;
    hasGameFilters: boolean;
    displayMode: "list" | "grid";
    order: "name" | "launch date";
    hasError: boolean;
    hasBlockingError: boolean;
    errorMessage: string;
    news: NewsItemEntity[];
    saveFilesAlertGame: GameEntity | null;
    mobileLaunchAlertGame: GameEntity | null;
}

interface Props {
    locale: string;
    analytics: AdobeAnalyticsContextData;
}

export default class Main extends Component<Props, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    private mainElement: HTMLElement | null = null;

    state: State = {
        games: [],
        displayedGames: [],
        gamesPlayed: [],
        // recommendedGames: [],
        userProgression: null,
        isLoading: true,
        filters: {
            name: "",
            brand: []
        },
        hasGameFilters: false,
        displayMode: App.isMobile.phone ? "list" : "grid",
        order: "launch date",
        hasError: false,
        hasBlockingError: false,
        errorMessage: "",
        news: [],
        saveFilesAlertGame: null,
        mobileLaunchAlertGame: null,
    };

    componentDidMount(): void {
        this.mainElement = document.getElementsByTagName('main').item(0);
        document.addEventListener('scroll', this.trackScrolling);

        this.getData();
    }

    componentWillUnmount(): void {
        document.removeEventListener('scroll', this.trackScrolling);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if (prevProps.locale !== this.props.locale) {
            this.getData();
        }

        if (
            prevState.games !== this.state.games ||
            prevState.filters !== this.state.filters ||
            prevState.order !== this.state.order
        ) {
            this.processDisplayedGames();
        }
    }

    render(): JSX.Element {
        if (this.state.isLoading) {
            return <Loading/>;
        } else if (this.state.hasBlockingError) {
            return (
                <div className="loading-container">
                    <ErrorMessage
                        message={<FormattedMessage id={this.state.errorMessage}/>}
                        onRefresh={this.getData}
                    />
                </div>
            );
        } else {
            if (this.context.subscriptionVerified && !this.context.user.isSubscribed) {
                return <LandingPage locale={this.props.locale}/>;
            }

            return (
                <div className="vault">
                    {
                        this.state.hasError &&
                        <ErrorAlert
                            message={<FormattedMessage id={this.state.errorMessage}/>}
                            onClose={this.closeError}
                        />
                    }

                    <>
                        <Switch>
                            <Route
                                exact={true}
                                path={`/:locale(${Object.getOwnPropertyNames(locales).join('|')})/cloudgaming`}
                                render={() => {
                                    if (!this.context.validateAnywhereAccess()) {
                                        return <Redirect to={`/${this.props.locale}`}/>
                                    }

                                    return (
                                        <Anywhere
                                            locale={this.props.locale}
                                            userContext={this.context}
                                        />
                                    );
                                }}
                            />

                            <Route render={() => {
                                if (this.context.validateAnywhereAccess() && !Anywhere.checkPreviousPageVisit()) {
                                    return <Redirect to={`/${this.props.locale}/cloudgaming`}/>;
                                }

                                return (
                                    <Home
                                        games={this.state.games}
                                        news={this.state.news}
                                        displayedGames={this.state.displayedGames}
                                        gamesPlayed={this.state.gamesPlayed}
                                        //recommendedGames={this.state.recommendedGames}
                                        filters={this.state.filters}
                                        order={this.state.order}
                                        displayMode={this.state.displayMode}
                                        hasGameFilters={this.state.hasGameFilters}
                                        setNameFilter={this.setNameFilter}
                                        setDisplayMode={this.setDisplayMode}
                                        setOrder={this.setOrder}
                                        toggleBrandFilter={this.toggleBrandFilter}
                                        handleGameAction={this.handleGameAction}
                                        analytics={this.props.analytics}
                                        locale={this.props.locale}
                                    />
                                );
                            }}/>
                        </Switch>

                        {
                            this.state.saveFilesAlertGame &&
                            <SaveFileAlertModal
                                game={this.state.saveFilesAlertGame}
                                activateGame={this.activateGame}
                                closeAlert={this.closeSaveFilesAlert}
                            />
                        }

                        {
                            this.state.mobileLaunchAlertGame &&
                            <MobileLaunchModal
                                game={this.state.mobileLaunchAlertGame.name}
                                closeAlert={this.closeMobileLaunchAlert}
                            />
                        }
                    </>
                </div>
            );
        }
    }

    trackScrolling = _.debounce(() => {
        if (this.mainElement) {
            let scrollBottom = Math.floor(this.mainElement.getBoundingClientRect().bottom);
            let windowHeight = Math.floor(window.innerHeight);

            if (this.mainElement.getBoundingClientRect().height > windowHeight && scrollBottom <= windowHeight) {
                this.props.analytics.track("clickevent", {location: "page", category: "scroll", action: "bottom page"});
                document.removeEventListener('scroll', this.trackScrolling);
            }
        }
    }, 500);

    setNameFilter = (value: string) => {
        this.trackSearchEvent(value);

        this.setState(state => ({
            filters: {
                ...state.filters,
                name: value
            }
        }));
    };

    trackSearchEvent = _.debounce((value: string) => {
        this.props.analytics.track("searchevent", {"searchKeyword": value});
    }, 1000);

    toggleBrandFilter = (value: string) => {
        this.props.analytics.track("clickevent", {location: "filter", category: "interaction", action: `brand : ${value}`});

        this.setState(state => {
            let brandFilters = state.filters.brand;

            if (value === "ALL") {
                brandFilters = [];
            } else {
                const filterIndex = brandFilters.indexOf(value);

                if (filterIndex > -1) {
                    brandFilters.splice(filterIndex, 1);
                } else {
                    brandFilters.push(value);
                }
            }

            return {
                filters: {
                    ...state.filters,
                    brand: brandFilters
                }
            };
        });
    };

    setDisplayMode = (value: "list" | "grid") => {
        this.props.analytics.track("clickevent", {location: "filter", category: "interaction", action: `display : ${value}`});
        this.setState({displayMode: value});
    };

    setOrder = (value: "name" | "launch date") => {
        this.props.analytics.track("clickevent", {location: "filter", category: "interaction", action: `sort by : ${value}`});
        this.setState({order: value});
    };

    processDisplayedGames = () => {
        this.setState(state => ({
            displayedGames: this.sortGames(this.filterGames(state.games)),
            hasGameFilters: this.hasGameFilters()
        }));
    };

    hasGameFilters = () => {
        let hasActiveFilters = false;
        Object.values(this.state.filters).forEach( value => {
            if(value.length > 0) {
                hasActiveFilters = true;
            }
        })
        return hasActiveFilters;
    }

    filterGames = (games: GameEntity[]): GameEntity[] => {
        if (this.state.filters.name) {
            let query = this.state.filters.name.toLowerCase().replace(/[^0-9a-z]+/g, '');

            games = games.filter(game => {
                return game.name.toLowerCase().replace(/[^0-9a-z]+/g, '').indexOf(query) !== -1
            });
        }

        if (this.state.filters.brand.length > 0) {
            games = games.filter(game => {
                if (game.brand) {
                    return this.state.filters.brand.includes(game.brand)
                }
                return false;
            });
        }

        return games;
    };

    sortGames = (games: GameEntity[]): GameEntity[] => {
        let sortedGames = games.slice();

        switch (this.state.order) {
            case 'name':
                sortedGames.sort((a, b) => {
                    let nameA = a.name.toLowerCase();
                    let nameB = b.name.toLowerCase();

                    if (nameA < nameB) {
                        return -1
                    }

                    if (nameA > nameB) {
                        return 1;
                    }

                    return 0;
                });
                break;

            case 'launch date':
                sortedGames.sort((a, b) => {
                    return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
                });
                break;
        }

        return sortedGames;
    };

    closeError = () => {
        this.setState({hasError: false});
    };

    closeSaveFilesAlert = () => {
        this.setState({saveFilesAlertGame: null});
    };

    closeMobileLaunchAlert = () => {
        this.setState({mobileLaunchAlertGame: null});
    };

    getVault = async(): Promise<AxiosResponse | null> => {
        if (this.context.user.ticketData.ticket && this.context.user.ticketData.sessionId) {
            return GamesService.getVault(this.context.user.ticketData.ticket, this.context.user.ticketData.sessionId, this.props.locale);
        }

        return null;
    };

    getNews = async(): Promise<AxiosResponse | null> => {
        if (this.context.user.ticketData.ticket && this.context.user.ticketData.sessionId) {
            return NewsService.getNews(
                this.context.user.ticketData.ticket,
                this.context.user.ticketData.sessionId,
                this.props.locale,
                this.context.userLocation.countryCode
            );
        }

        return null;
    };

    getGamesPlayed = () => {
        if (this.context.user.ticketData.ticket && this.context.user.ticketData.sessionId) {
            GamesPlayedService.getGamesPlayed(
                this.context.user.ticketData.ticket,
                this.context.user.ticketData.sessionId
            ).then((response: AxiosResponse) => {
                this.setState({
                    gamesPlayed: response.data.gamesPlayed,
                });
            }).catch(() => {
                console.log('Could not retrieve list of played games');
            })
        }
    };

    // getRecommendedGames = () => {
    //     if (
    //         this.context.user.ticketData.ticket &&
    //         this.context.user.ticketData.sessionId &&
    //         this.context.user.ticketData.userId
    //     ) {
    //         RecommendationsService.getRecommendations(
    //             this.context.user.ticketData.ticket,
    //             this.context.user.ticketData.sessionId,
    //             this.context.user.ticketData.userId
    //         ).then((response: AxiosResponse) => {
    //             if ('base_game' in response.data.recommendations) {
    //                 this.setState({
    //                     recommendedGames: response.data.recommendations['base_game']
    //                 });
    //             }
    //         }).catch(() => {
    //             console.log('Could not retrieve list of recommended games');
    //         });
    //     }
    // };

    private getData = () => {
        this.setState({isLoading: true, hasBlockingError: false, hasError: false}, this.loadVaultData);
    };

    private loadVaultData = async() => {
        this.getGamesPlayed();
        // this.getRecommendedGames();
        const getVaultPromise = this.getVault();
        const getNewsPromise = this.getNews();

        let gamesData: GameEntity[] = [];
        let progressionInfo:UserProgressionInfoEntity | null = null;
        let newsItems: NewsItemEntity[] = [];

        try {
            const vaultResponse = await getVaultPromise;
            if(vaultResponse) {
                gamesData = vaultResponse.data.games.map((game: GameEntity) => ({
                    ...game,
                    initialOwnership: game.ownership
                }));

                progressionInfo = vaultResponse.data.progressionInfo;
            }
            this.context.setSubscriptionStatus(true, progressionInfo);
        } catch (e) {
            this.processApiError(e, true);
            console.error('could not retrieve games');
        }

        try {
            const newsResponse = await getNewsPromise;
            if(newsResponse) {
                newsItems = newsResponse.data.news;
            }
        } catch (e) {
            console.error('could not retrieve news');
        }

        this.setState({
            isLoading: false,
            news: newsItems,
            games: this.sortGames(gamesData.map((game: GameEntity) => ({
                ...game,
                brand: game.brand ? game.brand.toLowerCase() : ""
            }))),
            userProgression: progressionInfo
        }, this.processDisplayedGames);
    };

    handleGameAction = (game: GameEntity, index?: number) => {
        if (!game.loading) {
            this.trackGameAction(game, index);

            if (game.ownership === 1) {
                this.upcClientGameAction(game);
                return;
            }

            if (game.hasSavefilesAlert && game.ownership === 2) {
                this.setState({saveFilesAlertGame: game});
                return;
            }

            this.activateGame(game);
        }
    };

    upcClientGameAction = (game: GameEntity) => {
        if (App.isMobile.any) {
            this.setState({mobileLaunchAlertGame: game});
            return;
        }

        if(game.requiresKeyOnLaunch) {
            window.open("uplay://open/game/" + game.uplayGameId, '_self');
        } else {
            window.open("uplay://launch/" + game.uplayGameId, '_self');
        }
    }

    //TODO: change tracking with the launch game feature update
    trackGameAction = (game: GameEntity, gameIndex?: number) => {
        if (gameIndex !== undefined) {
            if (game.ownership === 1) {
                this.props.analytics.track("clickevent", {location: `activated game : ${gameIndex + 1}`, category: "interaction", action: `launch game : ${game.name}`});
            } else {
                this.props.analytics.track("clickevent", {location: `activate game : ${gameIndex + 1}`, category: "interaction", action: `activate game : ${game.name}`});
            }
        }
    };

    activateGame = (activatedGame: GameEntity) => {
        let gameIndex = this.state.games.findIndex(game => game.id === activatedGame.id);

        this.setGameLoading(gameIndex, true, () => {
            if (this.context.user.ticketData.ticket && this.context.user.ticketData.sessionId) {
                GamesService.activateGame(this.context.user.ticketData.ticket, this.context.user.ticketData.sessionId, activatedGame.id)
                    .then(() => {
                        this.markGameActivated(gameIndex);
                        this.upcClientGameAction(activatedGame);
                    })
                    .catch(error => {
                        this.processApiError(error);
                    })
                    .finally(() => {
                        this.setGameLoading(gameIndex, false);
                    });
            }
        });
    };

    setGameLoading = (index: number, value: boolean = true, callback?: () => void) => {
        this.setState(prevState => (update(prevState, {
            games: {
                [index]: {
                    loading: {$set: value}
                }
            }
        })), callback);
    };

    markGameActivated = (index: number) => {
        this.setState(prevState => (update(prevState, {
            games: {
                [index]: {
                    ownership: {$set: 1}
                }
            }
        })));
    };

    processApiError = (error: AxiosError, isBlocking: boolean = false) => {
        let errorMessage: string = "message.error";
        let subscriptionError: boolean = false;

        if (error.response) {
            if (error.response.status === 403 || error.response.status === 401) {
                this.context.setSubscriptionStatus(false, null);
                subscriptionError = true;
            }
        } else {
            console.error('Request Error', error)
        }

        this.setState({
            hasError: true,
            hasBlockingError: isBlocking && !subscriptionError,
            errorMessage: errorMessage
        });
    };
}